import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft,
  ArrowUp,
  Check,
  Menu,
  Moon,
  MoreVertical,
  Plus,
  Square,
  SquarePen,
  Sun,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import LexCounselBadge from '../editor/LexCounselBadge';
import { Sources } from '../Sources';
import LexCounselContextHints, { LexCounselSharingStrip } from './LexCounselContextBar';
import LexCounselSuggestions from './LexCounselSuggestions';
import LexCounselPanelDailyBrief from './LexCounselPanelDailyBrief';
import LexCounselPanelGreeting from './LexCounselPanelGreeting';
import LexCounselInspector from './LexCounselInspector';
import { screenPlaceholder, LexCounselEditorModePicker, editorCounselModePlaceholder } from './LexCounselModeBar';
import type { LexCounselSuggestion } from '../../lib/lexCounselSuggestions';
import LexCounselThinkingIndicator from './LexCounselThinkingIndicator';
import LexCounselStreamingContent from './LexCounselStreamingContent';
import DraftChangePreview from './DraftChangePreview';
import FilingReviewScorecard from './FilingReviewScorecard';
import { applyDraftChange, discardDraftChange, insertDraftChangeAtCursor, useLexCounselStore } from '../../store/lexCounselStore';
import { runOneClickFilingReview, runFilingReviewEvaluate } from '../../lib/runFilingReview';
import { FILING_REVIEW_PROMPT } from '../../lib/filingReviewTypes';
import { useAuthSafe } from '../../contexts/AuthContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { isDocumentBlank } from '../../lib/draftingWorkflow/insertion';
import { hasRenderableCounselStreamContent, hasVisibleCounselStreamText } from '../../lib/counselStreamVisibility';
import { dispatchLexCounselSend, dispatchLexCounselStop } from '../../lib/lexCounselSend';
import { createCounselDraftTab, resolveCounselTabTitle } from '../../lib/lexCounselDraftTab';
import { AI_PANE_SPRING } from '../../lib/motionPresets';
import {
  LEX_COUNSEL_PANEL_OUTER_WIDTH,
  LEX_COUNSEL_PANEL_WIDTH,
  COUNSEL_PANEL_CLASS,
  COUNSEL_SURFACE,
  COUNSEL_ICON_BTN,
  COUNSEL_BUBBLE,
  COUNSEL_COMPOSER_CARD,
  COUNSEL_COMPOSER_BODY,
  COUNSEL_SEND_BTN,
  PANEL_BODY,
  PANEL_MUTED,
} from './lexCounselPanelUi';

function greetingName(displayName: string | null | undefined): string {
  const trimmed = displayName?.trim();
  if (!trimmed) return 'Counsel';
  const first = trimmed.split(/\s+/)[0];
  return first || trimmed;
}

export default function LexCounselPanel() {
  const auth = useAuthSafe();
  const isMobile = useIsMobile();
  const panelTab = useLexCounselStore((s) => s.panelTab);
  const setPanelTab = useLexCounselStore((s) => s.setPanelTab);
  const closePanel = useLexCounselStore((s) => s.closePanel);
  const messages = useLexCounselStore((s) => s.messages);
  const isThinking = useLexCounselStore((s) => s.isThinking);
  const inputDraft = useLexCounselStore((s) => s.inputDraft);
  const setInputDraft = useLexCounselStore((s) => s.setInputDraft);
  const showConversationList = useLexCounselStore((s) => s.showConversationList);
  const setShowConversationList = useLexCounselStore((s) => s.setShowConversationList);
  const conversations = useLexCounselStore((s) => s.conversations);
  const currentConversationId = useLexCounselStore((s) => s.currentConversationId);
  const startNewConversation = useLexCounselStore((s) => s.startNewConversation);
  const setMessages = useLexCounselStore((s) => s.setMessages);
  const setCurrentConversationId = useLexCounselStore((s) => s.setCurrentConversationId);
  const workspaceScreen = useLexCounselStore((s) => s.workspace.screen);
  const workspaceDraftText = useLexCounselStore((s) => s.workspace.draftText);
  const editorCounselMode = useLexCounselStore((s) => s.editorCounselMode);
  const setEditorCounselMode = useLexCounselStore((s) => s.setEditorCounselMode);
  const clearPendingChange = useLexCounselStore((s) => s.clearPendingChange);
  const updateDraftChangeStatus = useLexCounselStore((s) => s.updateDraftChangeStatus);
  const setDraftEditorPresentation = useLexCounselStore((s) => s.setDraftEditorPresentation);
  const panelTheme = useLexCounselStore((s) => s.panelTheme);
  const setPanelTheme = useLexCounselStore((s) => s.setPanelTheme);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const name = greetingName(
    auth?.userData?.displayName ?? auth?.user?.displayName ?? undefined,
  );
  const showChat = !showConversationList && panelTab === 'chat';
  const showInspector = !showConversationList && panelTab === 'context';
  const isEmpty = messages.length === 0;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, isThinking, messages[messages.length - 1]?.text]);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [menuOpen]);

  const syncTextareaHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '24px';
    el.style.height = `${Math.min(160, Math.max(24, el.scrollHeight))}px`;
  };

  useEffect(() => {
    syncTextareaHeight();
  }, [inputDraft]);

  useEffect(() => {
    const onFocusComposer = () => {
      textareaRef.current?.focus();
      syncTextareaHeight();
    };
    window.addEventListener('lexcounsel-focus-composer', onFocusComposer);
    return () => window.removeEventListener('lexcounsel-focus-composer', onFocusComposer);
  }, []);

  const showInlineComposerToggle =
    workspaceScreen === 'draft_editor' && isDocumentBlank(workspaceDraftText ?? '');

  const composerPlaceholder =
    workspaceScreen === 'draft_editor'
      ? editorCounselModePlaceholder(editorCounselMode)
      : screenPlaceholder(workspaceScreen);

  const handleSuggestionSelect = (item: LexCounselSuggestion) => {
    if (workspaceScreen === 'draft_editor') {
      if (item.editorCounselMode) {
        setEditorCounselMode(item.editorCounselMode);
      }
      if (item.runFilingReview || item.id === 'run-filing-review') {
        void (async () => {
          if (item.autoSend) {
            await runOneClickFilingReview();
            return;
          }
          await runFilingReviewEvaluate();
          setInputDraft(item.prompt || FILING_REVIEW_PROMPT);
          requestAnimationFrame(() => {
            textareaRef.current?.focus();
            syncTextareaHeight();
          });
        })();
        return;
      }
      if (item.autoSend) {
        dispatchLexCounselSend({
          text: item.prompt,
          scope: item.scope,
          source: 'panel',
        });
        return;
      }
      setInputDraft(item.prompt);
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
        syncTextareaHeight();
      });
      return;
    }
    dispatchLexCounselSend({
      text: item.prompt,
      scope: item.scope,
      source: 'panel',
    });
  };

  const sendCurrent = () => {
    if (!inputDraft.trim() || isThinking) return;
    window.dispatchEvent(new CustomEvent('lexcounsel-send', { detail: { text: inputDraft } }));
  };

  const captureDraftSelection = () => {
    window.dispatchEvent(new CustomEvent('lexcounsel-capture-draft-selection'));
  };

  const panelSurface = (
    <div
      className={`${COUNSEL_PANEL_CLASS} pointer-events-auto flex h-full w-full flex-col overflow-hidden ${COUNSEL_SURFACE} ${isMobile ? 'max-w-full rounded-none pb-[env(safe-area-inset-bottom)]' : 'w-[400px] shrink-0 rounded-[28px] border border-[var(--counsel-border)] shadow-[var(--counsel-shadow)]'}`}
      data-counsel-theme={panelTheme}
      style={isMobile ? undefined : { width: LEX_COUNSEL_PANEL_WIDTH }}
    >
        {/* Gemini-style minimal header */}
        <div className="flex shrink-0 items-center justify-between px-3 py-3">
          <button
            type="button"
            onClick={() => {
              if (showConversationList) {
                setShowConversationList(false);
              } else if (showInspector) {
                setPanelTab('chat');
              } else {
                setShowConversationList(true);
              }
            }}
            className={`rounded-full p-2.5 ${COUNSEL_ICON_BTN}`}
            aria-label={
              showConversationList || showInspector ? 'Back' : 'Chat history'
            }
          >
            {showConversationList || showInspector ? (
              <ArrowLeft className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={startNewConversation}
              className={`rounded-full p-2.5 ${COUNSEL_ICON_BTN}`}
              aria-label="New chat"
            >
              <SquarePen className="h-[18px] w-[18px]" />
            </button>
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className={`rounded-full p-2.5 ${COUNSEL_ICON_BTN}`}
                aria-label="More options"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
              <AnimatePresence>
                {menuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    className="absolute right-0 top-full z-20 mt-1 min-w-[180px] overflow-hidden rounded-xl border border-[var(--counsel-border)] bg-[var(--counsel-menu-bg)] py-1 shadow-[var(--counsel-shadow)]"
                  >
                    {showInlineComposerToggle && (
                      <button
                        type="button"
                        onClick={() => {
                          setDraftEditorPresentation('inline');
                          closePanel();
                          setMenuOpen(false);
                        }}
                        className="w-full px-4 py-2.5 text-left text-[13px] text-[var(--counsel-text)] hover:bg-[var(--counsel-hover)]"
                      >
                        Switch to inline composer
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setPanelTab('context');
                        setMenuOpen(false);
                      }}
                      className="w-full px-4 py-2.5 text-left text-[13px] text-[var(--counsel-text)] hover:bg-[var(--counsel-hover)]"
                    >
                      Context & diagnostics
                    </button>
                    <div className="my-1 border-t border-[var(--counsel-border-subtle)]" />
                    <button
                      type="button"
                      onClick={() => {
                        setPanelTheme('light');
                        setMenuOpen(false);
                      }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] text-[var(--counsel-text)] hover:bg-[var(--counsel-hover)]"
                    >
                      <Sun className="h-4 w-4 shrink-0 text-[var(--counsel-icon)]" />
                      <span className="flex-1">Light mode</span>
                      {panelTheme === 'light' ? (
                        <Check className="h-4 w-4 shrink-0 text-[var(--counsel-accent)]" />
                      ) : null}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPanelTheme('dark');
                        setMenuOpen(false);
                      }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] text-[var(--counsel-text)] hover:bg-[var(--counsel-hover)]"
                    >
                      <Moon className="h-4 w-4 shrink-0 text-[var(--counsel-icon)]" />
                      <span className="flex-1">Dark mode</span>
                      {panelTheme === 'dark' ? (
                        <Check className="h-4 w-4 shrink-0 text-[var(--counsel-accent)]" />
                      ) : null}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <button
              type="button"
              onClick={closePanel}
              className={`rounded-full p-2.5 ${COUNSEL_ICON_BTN}`}
              aria-label="Close Lex Counsel"
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>
        </div>

        {showConversationList ? (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-2 space-y-1">
            <button
              type="button"
              onClick={startNewConversation}
              className={`mb-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[var(--counsel-border)] bg-[var(--counsel-chip-bg)] px-4 py-2.5 text-[var(--counsel-text)] hover:bg-[var(--counsel-chip-hover)] ${PANEL_BODY}`}
            >
              <Plus className="h-4 w-4" /> New chat
            </button>
            {conversations.map((chat) => (
              <button
                key={chat.id}
                type="button"
                onClick={() => {
                  setCurrentConversationId(chat.id);
                  setShowConversationList(false);
                }}
                className={`w-full rounded-2xl p-3.5 text-left transition-colors ${
                  currentConversationId === chat.id
                    ? 'bg-[var(--counsel-active-bg)]'
                    : 'hover:bg-[var(--counsel-hover-subtle)]'
                }`}
              >
                <h4 className={`truncate font-medium ${PANEL_BODY}`}>{chat.title}</h4>
                <div className={`mt-1 flex items-center gap-2 ${PANEL_MUTED}`}>
                  {chat.updatedAt && format(new Date(chat.updatedAt), 'MMM d, yyyy')}
                  <span>·</span>
                  {chat.messageCount} messages
                </div>
              </button>
            ))}
          </div>
        ) : showInspector ? (
          <LexCounselInspector />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <FilingReviewScorecard />
            {isEmpty ? (
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 pb-5 custom-scrollbar">
                <div className="flex min-h-full flex-col justify-end">
                  {workspaceScreen === 'dashboard' ? (
                    <LexCounselPanelDailyBrief name={name} />
                  ) : (
                    <LexCounselPanelGreeting name={name} />
                  )}
                  {!isThinking && (
                    <LexCounselSuggestions
                      disabled={isThinking}
                      variant="chips"
                      onSelect={handleSuggestionSelect}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div
                ref={scrollRef}
                className="flex-1 min-h-0 overflow-y-auto px-4 pt-2 pb-4 flex flex-col gap-5"
              >
                {messages.map((msg, i) => {
                  const canShowStreamContent =
                    Boolean(msg.text) &&
                    (msg.streaming
                      ? hasRenderableCounselStreamContent(msg.text)
                      : hasVisibleCounselStreamText(msg.text));
                  const showThinking = Boolean(msg.streaming);

                  return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex w-full ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'user' ? (
                      <div className={`max-w-[88%] rounded-[22px] ${COUNSEL_BUBBLE} px-4 py-3 whitespace-pre-wrap ${PANEL_BODY}`}>
                        {msg.text}
                      </div>
                    ) : (
                      <div className="flex w-full min-w-0 flex-col gap-2">
                        <div
                          className={
                            msg.streaming
                              ? 'sticky top-0 z-10 -mx-1 flex flex-col gap-2 bg-[var(--counsel-bg)] px-1 pb-2 pt-0.5'
                              : 'flex flex-col gap-2'
                          }
                        >
                          <LexCounselBadge size="sm" active={Boolean(msg.streaming)} />
                          {showThinking ? (
                            <LexCounselThinkingIndicator
                              status={msg.thinkingStatus ?? 'Starting…'}
                              note={msg.activityNote}
                            />
                          ) : null}
                        </div>
                        {msg.modeSwitchNotice ? (
                          <p className={`rounded-xl border border-[var(--counsel-border)] bg-[var(--counsel-notice-bg)] px-3 py-2 ${PANEL_MUTED}`}>
                            {msg.modeSwitchNotice}
                          </p>
                        ) : null}
                        <div className={`w-full min-w-0 leading-relaxed lexcounsel-content ${PANEL_BODY}`}>
                          {canShowStreamContent ? (
                            <LexCounselStreamingContent
                              text={msg.text}
                              streaming={Boolean(msg.streaming)}
                            />
                          ) : null}
                          {msg.sources && msg.sources.length > 0 && (
                            <Sources
                              sources={msg.sources}
                              live={Boolean(msg.streaming && msg.sources.length > 0)}
                            />
                          )}
                          {msg.draftChange && !msg.streaming && (
                            <DraftChangePreview
                              change={msg.draftChange}
                              disabled={isThinking}
                              onApply={() => {
                                if (msg.draftChange!.scope === 'new_tab') {
                                  void (async () => {
                                    try {
                                      const { tabId } = await createCounselDraftTab(
                                        resolveCounselTabTitle(
                                          messages
                                            .slice(0, i)
                                            .reverse()
                                            .find((item) => item.role === 'user')?.text ?? 'New document',
                                        ),
                                      );
                                      requestAnimationFrame(() => {
                                        requestAnimationFrame(() => {
                                          const applied = applyDraftChange({
                                            ...msg.draftChange!,
                                            scope: 'document',
                                            tabId,
                                          });
                                          if (applied) {
                                            updateDraftChangeStatus(msg.draftChange!.id, 'accepted');
                                          }
                                        });
                                      });
                                    } catch {
                                      // The editor's tab-creation flow presents its own recoverable error state.
                                    }
                                  })();
                                  return;
                                }
                                const applied = applyDraftChange(msg.draftChange!);
                                if (applied) updateDraftChangeStatus(msg.draftChange!.id, 'accepted');
                                clearPendingChange();
                                setMessages((prev) =>
                                  prev.map((item, idx) =>
                                    idx === i && item.draftChange
                                      ? {
                                          ...item,
                                          draftChange: { ...item.draftChange, applied: true },
                                        }
                                      : item,
                                  ),
                                );
                              }}
                              onInsertAtCursor={() => {
                                insertDraftChangeAtCursor(msg.draftChange!);
                                clearPendingChange();
                                setMessages((prev) =>
                                  prev.map((item, idx) =>
                                    idx === i && item.draftChange
                                      ? {
                                          ...item,
                                          draftChange: { ...item.draftChange, applied: true },
                                        }
                                      : item,
                                  ),
                                );
                              }}
                              onDiscard={() => {
                                const change =
                                  useLexCounselStore.getState().activePendingChange?.id ===
                                  msg.draftChange!.id
                                    ? {
                                        ...msg.draftChange!,
                                        ...useLexCounselStore.getState().activePendingChange,
                                      }
                                    : msg.draftChange!;
                                discardDraftChange(change);
                                updateDraftChangeStatus(msg.draftChange!.id, 'rejected');
                                clearPendingChange();
                                setMessages((prev) =>
                                  prev.map((item, idx) =>
                                    idx === i ? { ...item, draftChange: undefined } : item,
                                  ),
                                );
                              }}
                              onRetry={() => {
                                const priorUser = messages
                                  .slice(0, i)
                                  .reverse()
                                  .find((item) => item.role === 'user');
                                if (!priorUser?.text.trim()) return;
                                dispatchLexCounselSend({
                                  text: priorUser.text,
                                  scope: msg.draftChange?.scope ?? 'paragraph',
                                  source: 'panel',
                                });
                              }}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                  );
                })}
                {isThinking &&
                !messages.some((msg) => msg.role === 'ai' && msg.streaming) ? (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex w-full justify-start"
                  >
                    <div className="flex w-full min-w-0 flex-col gap-2">
                      <LexCounselBadge size="sm" active />
                      <LexCounselThinkingIndicator status="Starting…" />
                    </div>
                  </motion.div>
                ) : null}
              </div>
            )}
          </div>
        )}

        {showChat && (
          <div className="shrink-0 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-2">
            {workspaceScreen === 'draft_editor' && !isEmpty && !isThinking ? (
              <div className="mb-2">
                <LexCounselSuggestions disabled={isThinking} variant="chips" onSelect={handleSuggestionSelect} />
              </div>
            ) : null}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (isThinking) return;
                sendCurrent();
              }}
            >
              <div className={COUNSEL_COMPOSER_CARD}>
                <LexCounselSharingStrip />
                <div className={COUNSEL_COMPOSER_BODY}>
                <div className="overflow-hidden">
                  <textarea
                    ref={textareaRef}
                    value={inputDraft}
                    onChange={(e) => setInputDraft(e.target.value)}
                    placeholder={composerPlaceholder}
                    rows={1}
                    disabled={isThinking}
                    className={`w-full resize-none bg-transparent leading-relaxed text-[var(--counsel-text)] placeholder:text-[var(--counsel-text-muted)] outline-none max-h-[160px] min-h-[24px] ${PANEL_BODY}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendCurrent();
                      }
                    }}
                  />
                  <div className="relative z-10 mt-3 grid grid-cols-[auto_1fr_auto] items-center gap-2">
                    <button
                      type="button"
                      className={`rounded-full p-2 ${COUNSEL_ICON_BTN}`}
                      aria-label="Add attachment"
                      tabIndex={-1}
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                    <div className="flex justify-center">
                      {workspaceScreen === 'draft_editor' ? (
                        <LexCounselEditorModePicker
                          mode={editorCounselMode}
                          onChange={setEditorCounselMode}
                          disabled={isThinking}
                        />
                      ) : null}
                    </div>
                    <div className="flex items-center justify-end">
                      {isThinking ? (
                        <button
                          type="button"
                          onClick={() => dispatchLexCounselStop()}
                          className={`flex h-9 w-9 items-center justify-center rounded-full ${COUNSEL_SEND_BTN} transition-opacity hover:opacity-90`}
                          aria-label="Stop generating"
                        >
                          <Square className="h-4 w-4 fill-current" />
                        </button>
                      ) : (
                        <button
                          type="submit"
                          disabled={!inputDraft.trim()}
                          className={`flex h-9 w-9 items-center justify-center rounded-full ${COUNSEL_SEND_BTN} transition-opacity hover:opacity-90 disabled:opacity-40`}
                          aria-label="Send message"
                        >
                          <ArrowUp className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </form>
            <LexCounselContextHints />
          </div>
        )}
      </div>
  );

  if (isMobile) {
    return (
      <motion.div
        role="complementary"
        aria-label="Lex Counsel"
        onMouseDownCapture={captureDraftSelection}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="pointer-events-none fixed inset-0 z-[70] flex bg-black/40 p-0"
      >
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="pointer-events-auto ml-auto flex h-full w-full"
        >
          {panelSurface}
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.aside
      role="complementary"
      aria-label="Lex Counsel"
      onMouseDownCapture={captureDraftSelection}
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: LEX_COUNSEL_PANEL_OUTER_WIDTH, opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      transition={AI_PANE_SPRING}
      className="flex h-full shrink-0 overflow-hidden py-3 pr-3"
    >
      {panelSurface}
    </motion.aside>
  );
}
